import { useState } from "react";
import { IconX, IconAlertTriangle } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useDeactivateWarehouseMutation, useWarehouseListData } from "../../../apis/warehouseApi";
import { useProfileData } from "../../../apis/profileApis";
import LoaderButton from "../../../components/common/LoaderButton";

type DeactivateWarehouseModalProps = {
  onClose: () => void;
  warehouseDetails: {
    id: string;
    name: string;
  };
};

export default function DeactivateWarehouseModal({
  onClose,
  warehouseDetails,
}: DeactivateWarehouseModalProps) {
  const [targetWarehouseId, setTargetWarehouseId] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  const allWarehouses = useSelector((state: any) => state?.warehouse?.data?.warehouses || []);

  // Filter out the source warehouse and only include active target warehouses (status is not false)
  const availableWarehouses = allWarehouses.filter(
    (wh: any) => wh.id !== warehouseDetails.id && wh.status !== false
  );

  const { mutate, isPending } = useDeactivateWarehouseMutation();
  const { refetch: refetchWarehouses } = useWarehouseListData();
  const { refetch: refetchProfile } = useProfileData();

  const handleDeactivate = () => {
    if (!targetWarehouseId) {
      setValidationError("Please select a target warehouse for asset transfer.");
      return;
    }

    setValidationError("");

    mutate(
      {
        id: warehouseDetails.id,
        transferWarehouseId: targetWarehouseId,
      },
      {
        onSuccess: () => {
          toast.success("Warehouse deactivated and assets transferred successfully.");
          refetchWarehouses();
          refetchProfile();
          onClose();
          // Remove active warehouse item from local storage to trigger switching to another warehouse
          localStorage.removeItem("WarehouseActiveRole");
        },
        onError: (error: any) => {
          console.error("Deactivation error:", error);
          const errorMsg = typeof error === "string" ? error : error?.message || "Failed to deactivate warehouse.";
          setValidationError(errorMsg);
          toast.error(errorMsg);
        },
      }
    );
  };

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center p-4 text-[#1E293B] animate-fade-in">
      <div className="rounded-2xl max-w-lg w-full relative bg-[#FAF8EC] shadow-2xl overflow-hidden transform transition-all duration-300 scale-95 md:scale-100">

        {/* Header */}
        <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 border-b border-[#D8D8D8]">
          <div className="flex items-center gap-2 text-red-600">
            <IconAlertTriangle size={24} />
            <h1 className="text-xl font-bold">Deactivate Warehouse</h1>
          </div>
          <button
            onClick={onClose}
            className="bg-[#F2F2F7] hover:bg-gray-200 transition-colors rounded-full p-2"
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 space-y-2">
            <p className="font-semibold">
              Warning: This action will deactivate and soft-delete "{warehouseDetails.name}".
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Once the warehouse is deactivated and assets are transferred, this action cannot be undone.</li>
              <li>All operational assets (Client Staff, Drivers, Vehicles, Store Integrations) will be transferred to the target warehouse.</li>
              <li>Warehouse layout structures (Racks, Levels, Bins, Floors, Pallets, Coverage, Delivery Routes) will be soft-deleted.</li>
              <li>This action is blocked if there are active inventory or pending orders in this warehouse.</li>
            </ul>
          </div>

          {availableWarehouses.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
              No other active warehouses are available to transfer assets. Please create and activate another warehouse first.
            </div>
          ) : (
            <div className="space-y-2">
              <label htmlFor="target-warehouse" className="block text-sm font-medium text-gray-700">
                Select Target Warehouse for Asset Transfer
              </label>
              <select
                id="target-warehouse"
                value={targetWarehouseId}
                onChange={(e) => {
                  setTargetWarehouseId(e.target.value);
                  setValidationError("");
                }}
                className="w-full px-4 py-2.5 text-sm rounded-full outline-none bg-white border border-[#D8D8D8] focus:border-red-500 transition-all"
                disabled={isPending}
              >
                <option value="">-- Choose Target Warehouse --</option>
                {availableWarehouses.map((wh: any) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {validationError && (
            <div className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg p-3 animate-pulse">
              {validationError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC]">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleDeactivate}
            disabled={isPending || availableWarehouses.length === 0 || !targetWarehouseId}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 py-2 text-sm font-semibold transition-all duration-200 ease-in-out transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md flex items-center justify-center min-w-[120px]"
          >
            {isPending ? <LoaderButton text="Deactivating..." /> : "Deactivate"}
          </button>
        </div>
      </div>
    </div>
  );
}
